import {
  BAY_LEVEL_FORE_AFT_FIELDS,
  BAY_LEVEL_YES_NO_FLAGS,
  OVD_BOUNDS,
  STAF_INTERMEDIATE_BAY_LEVEL_FIELDS,
  STAF_INTERMEDIATE_ROW_FIELDS,
} from "./consts";
import { IBayLevelFinding, IValidationInput, IValidationOutput, IValidator } from "../types";
import {
  isContainerLengthKey,
  isFiniteNumber,
  isForeAft,
  isOddIsoBayString,
  isPlainObject,
  isRowKey,
  isTierKey,
  isUnsetEnum,
  isYesNo,
} from "./patterns";

import { BayLevelEnum } from "open-vessel-definition";
import { createValidationOutput } from "../helpers/createValidationOutput";

const name = "bayLevelStructureValidator";
const okTranslationKey = "validators:structure.bayLevelStructure.ok";
const errorTranslationKey = "validators:structure.bayLevelStructure.error";

export type TBayLevelStructureCode =
  | "missing"
  | "wrongType"
  | "badEnum"
  | "badPattern"
  | "badParity"
  | "outOfBounds"
  | "duplicate"
  /** The entry's own key field (`size`, `isoRow`) differs from the dictionary key. Fixable */
  | "keyMismatch"
  /** A field that only exists while converting from STAF. Fixable */
  | "leakedField"
  /** Lashing bridge side written with the OVD 1.2.0 flat shape (`fore: 1, foreTiers`) that converts losslessly. Fixable */
  | "legacyShape"
  /** `pairedBay`, `reeferPlugs` or `doors` written as `0` or `null`, meaning "not set". Fixable */
  | "unsetEnum"
  /** Two fields contradict each other (a 1.2.0 lashing bridge with `fore: 0` and `foreTiers`, or mixed shapes) */
  | "inconsistent";

export interface IBayLevelStructureFinding extends IBayLevelFinding {
  code: TBayLevelStructureCode;
  /** Relative to the Bay-level, e.g. `infoByContLength.40.size`. `baysData.3` for an entry that is not an object */
  path: string;
}

export interface IBayLevelStructureOutput {
  findings: IBayLevelStructureFinding[];
}

export const FIXABLE_BAY_LEVEL_CODES: readonly TBayLevelStructureCode[] = [
  "keyMismatch",
  "leakedField",
  "legacyShape",
  "unsetEnum",
];

/**
 * The shape of every Bay-level except its slots (see `slotStructureValidator`):
 * `isoBay` and `level`, uniqueness, the container-length dictionaries and their
 * `size`, the per-row dictionary and its `isoRow`, tier-height keys, enums and flags,
 * bulkheads and lashing bridges, and fields left over from a STAF conversion.
 *
 * It never throws. Entries of `baysData` that are not objects are reported and skipped.
 */
export const bayLevelStructureValidator: IValidator<IBayLevelStructureOutput> = {
  name,
  okTranslationKey,
  errorTranslationKey,
  fn: bayLevelStructureValidatorFn,
  severity: "error",
  stage: "structure",
};

export function bayLevelStructureValidatorFn({
  data: { json },
}: IValidationInput): IValidationOutput<IBayLevelStructureOutput> {
  const findings: IBayLevelStructureFinding[] = [];
  const baysData: unknown[] = Array.isArray(json?.baysData) ? json.baysData : [];
  const seen = new Set<string>();

  baysData.forEach((entry, i) => {
    if (!isPlainObject(entry)) {
      findings.push({
        code: "wrongType",
        path: `baysData.${i}`,
        isoBay: "",
        level: 0 as BayLevelEnum,
      });
      return;
    }

    const isoBay = typeof entry["isoBay"] === "string" ? entry["isoBay"] : "";
    const level = entry["level"] as BayLevelEnum;
    const flag = (code: TBayLevelStructureCode, path: string) =>
      findings.push({ code, path, isoBay, level });

    checkIdentity(entry, flag);

    const key = `${isoBay}-${level}`;
    if (seen.has(key)) flag("duplicate", "isoBay");
    seen.add(key);

    checkLengthDictionary(entry["infoByContLength"], "infoByContLength", true, flag);
    checkPerRowInfo(entry["perRowInfo"], flag);
    checkEnumsAndFlags(entry, flag);
    checkBulkhead(entry["bulkhead"], flag);
    checkLashingBridges(entry["lashingBridges"], flag);

    STAF_INTERMEDIATE_BAY_LEVEL_FIELDS.forEach((f) => {
      if (entry[f] !== undefined) flag("leakedField", f);
    });
  });

  return createValidationOutput(bayLevelStructureValidator, findings, { findings });
}

type TFlag = (code: TBayLevelStructureCode, path: string) => void;

function checkIdentity(entry: Record<string, unknown>, flag: TFlag): void {
  const isoBay = entry["isoBay"];
  if (isoBay === undefined) flag("missing", "isoBay");
  else if (typeof isoBay !== "string") flag("wrongType", "isoBay");
  else if (!/^\d{3}$/.test(isoBay)) flag("badPattern", "isoBay");
  else if (!isOddIsoBayString(isoBay)) flag("badParity", "isoBay");
  else if (Number(isoBay) < OVD_BOUNDS.BAY_MIN || Number(isoBay) >= OVD_BOUNDS.BAY_MAX_EXCLUSIVE)
    flag("outOfBounds", "isoBay");

  const level = entry["level"];
  if (level === undefined) flag("missing", "level");
  else if (
    level !== BayLevelEnum.ABOVE &&
    level !== BayLevelEnum.BELOW &&
    level !== BayLevelEnum.TWINDECK
  )
    flag("badEnum", "level");
}

/** `infoByContLength` and `rowInfoByLength`: keys are container lengths and `size` repeats the key */
function checkLengthDictionary(dict: unknown, path: string, required: boolean, flag: TFlag): void {
  if (dict === undefined) {
    if (required) flag("missing", path);
    return;
  }
  if (!isPlainObject(dict)) return flag("wrongType", path);

  Object.keys(dict).forEach((k) => {
    const ep = `${path}.${k}`;
    if (!isContainerLengthKey(k)) return flag("badEnum", ep);

    const entry = dict[k];
    if (!isPlainObject(entry)) return flag("wrongType", ep);

    if (entry["size"] !== Number(k)) flag("keyMismatch", `${ep}.size`);

    (["lcg", "rowWeight", "bottomWeight", "minBottomHeight"] as const).forEach((f) => {
      if (entry[f] !== undefined && !isFiniteNumber(entry[f])) flag("wrongType", `${ep}.${f}`);
    });
  });
}

function checkTierHeights(dict: unknown, path: string, flag: TFlag): void {
  if (dict === undefined) return;
  if (!isPlainObject(dict)) return flag("wrongType", path);

  Object.keys(dict).forEach((k) => {
    if (!isTierKey(k)) flag("badPattern", `${path}.${k}`);
    if (!isFiniteNumber(dict[k])) flag("wrongType", `${path}.${k}`);
  });
}

function checkPerRowInfo(pri: unknown, flag: TFlag): void {
  if (pri === undefined) return;
  if (!isPlainObject(pri)) return flag("wrongType", "perRowInfo");

  const common = pri["common"];
  if (common !== undefined) {
    if (!isPlainObject(common)) flag("wrongType", "perRowInfo.common");
    else {
      (["bottomBase", "maxHeight"] as const).forEach((f) => {
        if (common[f] !== undefined && !isFiniteNumber(common[f]))
          flag("wrongType", `perRowInfo.common.${f}`);
      });
      checkTierHeights(common["minTierHeights"], "perRowInfo.common.minTierHeights", flag);
    }
  }

  const each = pri["each"];
  if (each === undefined) return;
  if (!isPlainObject(each)) return flag("wrongType", "perRowInfo.each");

  Object.keys(each).forEach((row) => {
    const rp = `perRowInfo.each.${row}`;
    if (!isRowKey(row)) flag("badPattern", rp);

    const entry = each[row];
    if (!isPlainObject(entry)) return flag("wrongType", rp);

    if (entry["isoRow"] !== row) flag("keyMismatch", `${rp}.isoRow`);

    (["tcg", "bottomBase", "maxHeight"] as const).forEach((f) => {
      if (entry[f] !== undefined && !isFiniteNumber(entry[f])) flag("wrongType", `${rp}.${f}`);
    });

    checkLengthDictionary(entry["rowInfoByLength"], `${rp}.rowInfoByLength`, false, flag);
    checkTierHeights(entry["minTierHeights"], `${rp}.minTierHeights`, flag);

    STAF_INTERMEDIATE_ROW_FIELDS.forEach((f) => {
      if (entry[f] !== undefined) flag("leakedField", `${rp}.${f}`);
    });
  });
}

function checkEnumsAndFlags(entry: Record<string, unknown>, flag: TFlag): void {
  BAY_LEVEL_FORE_AFT_FIELDS.forEach((f) => {
    if (entry[f] === undefined || isForeAft(entry[f])) return;
    flag(isUnsetEnum(entry[f]) ? "unsetEnum" : "badEnum", f);
  });

  BAY_LEVEL_YES_NO_FLAGS.forEach((f) => {
    if (entry[f] !== undefined && !isYesNo(entry[f])) flag("badEnum", f);
  });

  if (entry["reeferPlugLimit"] !== undefined && !isFiniteNumber(entry["reeferPlugLimit"]))
    flag("wrongType", "reeferPlugLimit");

  (["label20", "label40"] as const).forEach((f) => {
    if (entry[f] !== undefined && typeof entry[f] !== "string") flag("wrongType", f);
  });
}

function checkBulkhead(bh: unknown, flag: TFlag): void {
  if (bh === undefined) return;
  if (!isPlainObject(bh)) return flag("wrongType", "bulkhead");

  (["fore", "aft"] as const).forEach((side) => {
    if (bh[side] !== undefined && !isYesNo(bh[side])) flag("badEnum", `bulkhead.${side}`);
    const lcg = bh[`${side}Lcg`];
    if (lcg !== undefined && !isFiniteNumber(lcg)) flag("wrongType", `bulkhead.${side}Lcg`);
  });
}

/**
 * OVD 1.3.0: `{ fore?: ILashingBridge; aft?: ILashingBridge }`, where a bridge is
 * `{ tiers, height?, lcg?, perRow?: { [row]: { tiers, height? } } }`.
 * Lenient on purpose (any structure error disables the CG pipeline): non-integer tiers are accepted.
 * Sides written with the OVD 1.2.0 flat shape are `legacyShape` (fixable) when they convert losslessly.
 */
function checkLashingBridges(lb: unknown, flag: TFlag): void {
  if (lb === undefined) return;
  if (!isPlainObject(lb)) return flag("wrongType", "lashingBridges");

  (["fore", "aft"] as const).forEach((side) => {
    const path = `lashingBridges.${side}`;
    const value = lb[side];
    const tiersKey = `${side}Tiers`;
    const lcgKey = `${side}Lcg`;
    const hasLegacyKeys = lb[tiersKey] !== undefined || lb[lcgKey] !== undefined;

    if (isPlainObject(value)) {
      if (hasLegacyKeys) flag("inconsistent", path);
      checkLashingBridge(value, path, flag);
      return;
    }

    if (typeof value === "number" || (value === undefined && hasLegacyKeys)) {
      flag(
        isConvertibleLegacySide(value, lb[tiersKey], lb[lcgKey]) ? "legacyShape" : "inconsistent",
        path,
      );
      return;
    }

    if (value !== undefined) flag("wrongType", path);
  });
}

function isConvertibleLegacySide(on: unknown, tiers: unknown, lcg: unknown): boolean {
  if (on === 0) return tiers === undefined && lcg === undefined;
  if (on !== 1) return false;
  return isFiniteNumber(tiers) && tiers >= 0 && (lcg === undefined || isFiniteNumber(lcg));
}

function checkLashingBridge(bridge: Record<string, unknown>, path: string, flag: TFlag): void {
  checkLashingBridgeHeight(bridge, path, flag);

  if (bridge["lcg"] !== undefined && !isFiniteNumber(bridge["lcg"]))
    flag("wrongType", `${path}.lcg`);

  const perRow = bridge["perRow"];
  if (perRow === undefined) return;
  if (!isPlainObject(perRow)) return flag("wrongType", `${path}.perRow`);

  Object.keys(perRow).forEach((row) => {
    const rowPath = `${path}.perRow.${row}`;
    if (!isRowKey(row)) flag("badPattern", rowPath);

    const h = perRow[row];
    if (!isPlainObject(h)) flag("wrongType", rowPath);
    else checkLashingBridgeHeight(h, rowPath, flag);
  });
}

function checkLashingBridgeHeight(h: Record<string, unknown>, path: string, flag: TFlag): void {
  const tiers = h["tiers"];
  if (tiers === undefined) flag("missing", `${path}.tiers`);
  else if (!isFiniteNumber(tiers)) flag("wrongType", `${path}.tiers`);
  else if (tiers < 0) flag("outOfBounds", `${path}.tiers`);

  const height = h["height"];
  if (height === undefined) return;
  if (!isFiniteNumber(height)) flag("wrongType", `${path}.height`);
  else if (height < 0) flag("outOfBounds", `${path}.height`);
}
